import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, useMemo } from "react";
import { fetchInnovationItems, fetchInnovationRuns, fetchInnovationRotations, fetchInnovationStats, fmtRelative, triggerInnovation, fmtDateOnly } from "../lib/api";
import { Brain, ExternalLink, Search, Zap, Bookmark, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import HowItWorks from "../components/HowItWorks";
import PlatformIdeaGenerator from "../components/PlatformIdeaGenerator";
import PlatformCrossFusion from "../components/PlatformCrossFusion";

const STATUS_COLOR = {
  active: "#10B981",
  rotated: "#F59E0B",
  expired: "#EF4444",
};

function host(u) {
  try {
    return new URL(u).host.replace(/^www\./, "");
  } catch {
    return u;
  }
}

function ScoreBar({ value }) {
  const v = Math.max(0, Math.min(100, Number(value || 0)));
  return (
    <span className="rank-bar" title={`${v.toFixed(0)}/100`}>
      <span style={{ width: `${v}%` }} />
    </span>
  );
}

function InnovationCard({ item }) {
  const [saved, setSaved] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [busy, setBusy] = useState(false);

  // Phase 30.6 — 30-day retention badge. Warns 5d before expiry.
  const daysUntilExpiry = (() => {
    if (!item.expires_at) return null;
    const end = new Date(item.expires_at).getTime();
    if (isNaN(end)) return null;
    return Math.ceil((end - Date.now()) / (24 * 60 * 60 * 1000));
  })();

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/saves", {
        layer: "innovation",
        ref_id: String(item.number),
        title: item.title,
        text: item.summary,
        payload: {
          number: item.number, category: item.category,
          source_1: item.source_1, source_2: item.source_2,
          novelty: item.novelty, impact: item.impact, feasibility: item.feasibility,
        },
      });
      setSaved(true);
      setSavedId(data?.id || null);
      toast.success(`SAVED · #${String(item.number).padStart(3, "0")}`,
        { description: "Find it in My Library." });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "unknown error";
      toast.error("SAVE_FAILED", { description: msg });
    } finally {
      setBusy(false);
    }
  };

  const unsave = async () => {
    if (!savedId) return;
    setBusy(true);
    try {
      await api.delete(`/saves/${savedId}`);
      setSaved(false);
      setSavedId(null);
      toast.success("REMOVED_FROM_LIBRARY");
    } catch (e) {
      toast.error("DELETE_FAILED", { description: e?.response?.data?.detail || e?.message });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      data-testid="innovation-card"
      className="grid grid-cols-12 gap-3 p-3 border-b border-[#161616] hover:bg-[#0E0E0E] items-start"
    >
      <div className="col-span-1">
        <div className="font-mono text-amber-300 text-xl tabnum leading-none">
          #{String(item.number).padStart(3, "0")}
        </div>
        <div className="text-[10px] uppercase font-mono text-zinc-500 mt-1">
          {item.category}
        </div>
      </div>
      <div className="col-span-7 min-w-0">
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider">
          <span
            className="dot"
            style={{ background: STATUS_COLOR[item.status] || "#71717A" }}
          />
          <span className="text-zinc-500">{item.status}</span>
          {daysUntilExpiry !== null && daysUntilExpiry >= 0 && (
            <span
              data-testid={`innov-expiry-${item.id}`}
              className={
                "ml-2 border px-1.5 py-0.5 text-[9.5px] font-mono tabnum " +
                (daysUntilExpiry <= 5
                  ? "border-amber-400/60 bg-amber-500/10 text-amber-300"
                  : "border-[#27272A] bg-[#0A0A0A] text-zinc-400")
              }
              title={`Expires ${fmtDateOnly(item.expires_at)} · 30-day retention`}
            >
              {daysUntilExpiry === 0
                ? "EXPIRES_TODAY"
                : daysUntilExpiry === 1
                ? "1d LEFT"
                : `${daysUntilExpiry}d LEFT`}
            </span>
          )}
          <span className="ml-auto text-zinc-600 tabnum">
            {fmtRelative(item.created_at)}
          </span>
          {saved ? (
            <button
              data-testid={`unsave-innov-${item.id}`}
              onClick={unsave}
              disabled={busy}
              title="Remove from My Library"
              className="ml-2 inline-flex items-center gap-1 px-2 h-6 text-[10px] font-mono uppercase tracking-wider border border-rose-400/60 text-rose-300 hover:bg-rose-500/10 disabled:opacity-50"
            >
              <Trash2 size={10} /> REMOVE
            </button>
          ) : (
            <button
              data-testid={`save-innov-${item.id}`}
              onClick={save}
              disabled={busy}
              title="Save to My Library"
              className="ml-2 inline-flex items-center gap-1 px-2 h-6 text-[10px] font-mono uppercase tracking-wider border border-purple-400/60 text-purple-300 hover:bg-purple-500/10 disabled:opacity-50"
            >
              <Bookmark size={10} /> {busy ? "…" : "SAVE"}
            </button>
          )}
        </div>
        <div className="mt-1 text-white text-[14px] leading-snug">
          {item.title}
        </div>
        <div className="mt-1 text-zinc-400 text-[12px] leading-snug">
          {item.summary}
        </div>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 items-center text-[11px] font-mono">
          <a
            data-testid={`innovation-source-1-${item.id}`}
            href={item.source_1}
            target="_blank"
            rel="noopener noreferrer"
            className="src-link inline-flex items-center gap-1"
          >
            <span className="text-zinc-600">S1</span>
            <span className="truncate max-w-[220px]">{host(item.source_1)}</span>
            <ExternalLink size={10} />
          </a>
          <a
            data-testid={`innovation-source-2-${item.id}`}
            href={item.source_2}
            target="_blank"
            rel="noopener noreferrer"
            className="src-link inline-flex items-center gap-1"
          >
            <span className="text-zinc-600">S2</span>
            <span className="truncate max-w-[220px]">{host(item.source_2)}</span>
            <ExternalLink size={10} />
          </a>
          {item.source_3 && (
            <a
              data-testid={`innovation-source-3-${item.id}`}
              href={item.source_3}
              target="_blank"
              rel="noopener noreferrer"
              className="src-link inline-flex items-center gap-1"
            >
              <span className="text-zinc-600">S3</span>
              <span className="truncate max-w-[220px]">{host(item.source_3)}</span>
              <ExternalLink size={10} />
            </a>
          )}
        </div>
        {item.research_summary && (
          <details data-testid={`innovation-summary-${item.id}`} className="mt-2 group">
            <summary className="cursor-pointer text-[10px] font-mono uppercase tracking-wider text-amber-300 hover:text-white select-none inline-flex items-center gap-1">
              <FileText size={11} /> RESEARCH_SUMMARY →
            </summary>
            <p className="mt-2 text-[12px] text-zinc-300 leading-relaxed border-l-2 border-amber-400/40 pl-3 bg-amber-500/[0.03] py-2">
              {item.research_summary}
            </p>
          </details>
        )}
      </div>
      <div className="col-span-4 font-mono text-[11px] grid grid-cols-2 gap-x-3 gap-y-1 tabnum">
        <div className="flex items-center gap-2">
          <span className="text-purple-300 w-10">NOV</span>
          <ScoreBar value={item.novelty} />
          <span className="w-9 text-right">{item.novelty.toFixed(0)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-amber-300 w-10">IMP</span>
          <ScoreBar value={item.impact} />
          <span className="w-9 text-right">{item.impact.toFixed(0)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-emerald-300 w-10">FEAS</span>
          <ScoreBar value={item.feasibility} />
          <span className="w-9 text-right">{item.feasibility.toFixed(0)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-zinc-500 w-10">SRC</span>
          <ScoreBar value={item.source_rank} />
          <span className="w-9 text-right">{item.source_rank.toFixed(0)}</span>
        </div>
        <div className="col-span-2 flex items-center gap-2">
          <span className="text-white w-10">RANK</span>
          <ScoreBar value={item.item_rank} />
          <span className="w-9 text-right text-white">
            {item.item_rank.toFixed(1)}
          </span>
        </div>
      </div>
    </div>
  );
}

export default function InnovationPage() {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("active");
  const [sort, setSort] = useState("item_rank");
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");

  const { data: stats } = useQuery({
    queryKey: ["innov-stats"],
    queryFn: fetchInnovationStats,
    refetchInterval: 10000,
  });
  const { data: items = [] } = useQuery({
    queryKey: ["innov-items", status, sort],
    queryFn: () => fetchInnovationItems({ status, sort, limit: 200 }),
    refetchInterval: 10000,
  });
  const { data: runs = [] } = useQuery({
    queryKey: ["innov-runs"],
    queryFn: () => fetchInnovationRuns({ limit: 8 }),
    refetchInterval: 10000,
  });
  const { data: rotations = [] } = useQuery({
    queryKey: ["innov-rotations"],
    queryFn: () => fetchInnovationRotations({ limit: 20 }),
    refetchInterval: 15000,
  });

  const categories = useMemo(() => {
    const set = new Set(items.map((i) => i.category).filter(Boolean));
    return ["all", ...Array.from(set).sort()];
  }, [items]);

  const filtered = useMemo(() => {
    let v = items;
    if (cat !== "all") v = v.filter((i) => i.category === cat);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      v = v.filter((i) => {
        const haystack = [
          i.title,
          i.summary,
          i.category,
          i.research_summary,
          host(i.source_1 || ""),
          host(i.source_2 || ""),
          host(i.source_3 || ""),
          i.publisher_1,
          i.publisher_2,
          i.publisher_3,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(needle);
      });
    }
    return v;
  }, [items, cat, q]);

  const trigger = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await triggerInnovation({ target_count: 50 });
      toast.success("INNOVATION_SCOUT QUEUED", {
        description: "Scanning frontier signals · refresh in ~30s.",
      });
      setTimeout(() => qc.invalidateQueries({ queryKey: ["innov-stats"] }), 1500);
    } catch (e) {
      toast.error("RUN_FAILED", { description: e?.message || "" });
    } finally {
      setBusy(false);
    }
  };

  const liveCount = stats?.live_count ?? 0;
  const capPct = Math.min(100, (liveCount / 500) * 100);

  return (
    <div data-testid="innovation-page" className="p-4 space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-purple-300 font-mono">
            <Brain size={12} />
            MINDSTEIN_SCOUT
          </div>
          <h1 className="font-mono text-2xl tracking-tight mt-1">
            INNOVATION_FEED
          </h1>
          <p className="text-zinc-500 text-[12px] mt-1 max-w-2xl">
            Frontier signals from preprints, labs and R&D fronts. Numbered,
            ranked, capped at <b className="text-purple-300">500 live items</b> ·
            up to <b className="text-amber-300">50 new signals a day</b> ·
            <b className="text-emerald-300"> 30-day retention</b> · oldest
            rotate out automatically once the cap is reached.
          </p>
        </div>
        <div className="flex items-end gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
              LIVE · CAP_500
            </div>
            <div className="text-3xl font-mono tabnum text-purple-300">
              {liveCount}
              <span className="text-zinc-500 text-base ml-1">/ 500</span>
            </div>
            <div className="mt-1 h-[3px] w-44 bg-[#1a1a1a]">
              <div
                className="bg-purple-400"
                style={{ width: `${capPct}%`, height: "100%" }}
              />
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
              TODAY
            </div>
            <div className="text-3xl font-mono tabnum text-amber-300">
              {stats?.today_count ?? 0}
              <span className="text-zinc-500 text-base ml-1">
                / {stats?.today_target ?? 50}
              </span>
            </div>
          </div>
          <button
            data-testid="innovation-run-button"
            onClick={trigger}
            disabled={busy}
            className="h-9 px-3 text-[11px] font-mono uppercase tracking-wider border border-purple-400 bg-purple-400 text-black hover:bg-purple-300 disabled:opacity-50 flex items-center gap-2"
          >
            <Zap size={12} />
            {busy ? "QUEUEING…" : "SCOUT_NOW"}
          </button>
        </div>
      </div>

      <HowItWorks
        id="innovation"
        tag="FRONTIER · 50/DAY · CAP 500 · 30-DAY RETENTION"
        headline="A separate scout looks beyond the 10 main topics — surfacing preprints, lab spinouts and R&D signals that don't fit a single library."
        steps={[
          { accent: "purple", label: "Browse the feed", body: "Each numbered card is a fresh signal with two verified references and a novelty + feasibility score." },
          { accent: "amber", label: "Filter & sort", body: "Use STATUS / SORT / CATEGORY button pills below to slice by novelty, feasibility, or category." },
          { accent: "emerald", label: "Save anything", body: "Hit the bookmark icon to copy it to MY_LIB. It stays forever — even after the original rotates out." },
          { accent: "sky", label: "Remix in the Bot", body: "Open BOT, attach the innovation refs from the right rail, pick a mode (combine / compare / remix), and run." },
          { accent: "rose", label: "Rotate / refresh", body: "SCOUT_NOW forces a manual sweep. Items >30d auto-expire; the 500-cap auto-rotates oldest first." },
        ]}
      />

      <PlatformIdeaGenerator />

      <PlatformCrossFusion />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Stat label="ROTATED" value={stats?.rotated_count ?? 0} sub="cap_overflow" />
        <Stat label="EXPIRED" value={stats?.expired_count ?? 0} sub="30d_window" />
        <Stat label="RUNS_TODAY" value={stats?.runs_today ?? 0} sub={`${stats?.runs_total ?? 0} total`} />
        <Stat label="AVG_NOV" value={(stats?.avg_novelty ?? 0).toFixed(1)} sub="active items" />
        <Stat label="CATEGORIES" value={categories.length - 1} sub="active tags" />
      </div>

      {/* filters */}
      <div className="flex flex-wrap items-center gap-2 border-y border-[#27272A] px-3 py-2 bg-[#0E0E0E]">
        <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
          STATUS
        </span>
        {["active", "rotated", "expired", "all"].map((s) => (
          <button
            key={s}
            data-testid={`innov-status-${s}`}
            onClick={() => setStatus(s)}
            className={
              "h-7 px-2 text-[10px] font-mono uppercase tracking-wider border " +
              (status === s
                ? "border-purple-400 bg-purple-400 text-black"
                : "border-[#27272A] text-zinc-400 hover:text-white hover:bg-[#141414]")
            }
          >
            {s}
          </button>
        ))}
        <span className="ml-4 text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
          SORT
        </span>
        {[
          { v: "item_rank", l: "RANK" },
          { v: "novelty", l: "NOVELTY" },
          { v: "number", l: "NUMBER" },
          { v: "created_at", l: "NEWEST" },
        ].map((s) => (
          <button
            key={s.v}
            data-testid={`innov-sort-${s.v}`}
            onClick={() => setSort(s.v)}
            className={
              "h-7 px-2 text-[10px] font-mono uppercase tracking-wider border " +
              (sort === s.v
                ? "border-purple-400 bg-purple-400 text-black"
                : "border-[#27272A] text-zinc-400 hover:text-white hover:bg-[#141414]")
            }
          >
            {s.l}
          </button>
        ))}
        <span className="ml-4 text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
          CATEGORY
        </span>
        {categories.map((c) => (
          <button
            key={c}
            data-testid={`innov-category-${c}`}
            onClick={() => setCat(c)}
            className={
              "h-7 px-2 text-[10px] font-mono uppercase tracking-wider border " +
              (cat === c
                ? "border-purple-400 bg-purple-400 text-black"
                : "border-[#27272A] text-zinc-400 hover:text-white hover:bg-[#141414]")
            }
          >
            {c}
          </button>
        ))}

        <label className="ml-auto inline-flex items-center gap-2 h-7 px-2 border border-[#27272A] bg-[#0A0A0A]">
          <Search size={12} className="text-zinc-500" />
          <input
            data-testid="innov-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="SEARCH · TITLE · SUMMARY · SOURCE · CATEGORY"
            className="bg-transparent outline-none text-[11px] font-mono text-white placeholder:text-zinc-600 w-56"
          />
        </label>
        <span className="text-[10px] font-mono text-zinc-500 tabnum">
          {filtered.length} / {items.length}
        </span>
      </div>

      {/* feed */}
      <section className="border border-[#27272A] bg-[#0A0A0A]">
        {filtered.length === 0 ? (
          <div className="grid-bg p-10 text-center text-[12px] text-zinc-500 font-mono">
            NO_INNOVATION_SIGNALS · trigger SCOUT_NOW or wait for scheduler
          </div>
        ) : (
          filtered.map((it) => <InnovationCard key={it.id} item={it} />)
        )}
      </section>

      {/* runs + rotations side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <section className="border border-[#27272A]">
          <div className="px-3 py-2 border-b border-[#27272A] text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">
            RECENT_RUNS
          </div>
          <table className="w-full text-[11px] font-mono">
            <thead className="text-zinc-500">
              <tr className="border-b border-[#161616]">
                <th className="text-left p-2">WHEN</th>
                <th className="text-left p-2">STATUS</th>
                <th className="text-right p-2">INS</th>
                <th className="text-right p-2">DUP</th>
                <th className="text-right p-2">ROT</th>
                <th className="text-right p-2">DUR</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id} className="border-b border-[#0E0E0E] hover:bg-[#0E0E0E]">
                  <td className="p-2 text-zinc-400">{fmtRelative(r.started_at)}</td>
                  <td className="p-2 uppercase">
                    <span
                      className="dot"
                      style={{
                        background:
                          r.status === "completed"
                            ? "#10B981"
                            : r.status === "partial"
                            ? "#F59E0B"
                            : r.status === "failed"
                            ? "#EF4444"
                            : "#3B82F6",
                      }}
                    />
                    {r.status}
                  </td>
                  <td className="p-2 text-right tabnum text-emerald-400">
                    {r.ideas_inserted}
                  </td>
                  <td className="p-2 text-right tabnum text-amber-400">
                    {r.ideas_deduped}
                  </td>
                  <td className="p-2 text-right tabnum text-purple-400">
                    {r.rotated_out}
                  </td>
                  <td className="p-2 text-right tabnum">{r.duration_ms}ms</td>
                </tr>
              ))}
              {runs.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-4 text-center text-zinc-500">
                    NO_RUNS_YET
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
        <section className="border border-[#27272A]">
          <div className="px-3 py-2 border-b border-[#27272A] text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">
            ROTATION_LOG · last 20
          </div>
          <table className="w-full text-[11px] font-mono">
            <thead className="text-zinc-500">
              <tr className="border-b border-[#161616]">
                <th className="text-left p-2">WHEN</th>
                <th className="text-left p-2">ITEM</th>
                <th className="text-left p-2">REASON</th>
              </tr>
            </thead>
            <tbody>
              {rotations.map((r) => (
                <tr key={r.id} className="border-b border-[#0E0E0E] hover:bg-[#0E0E0E]">
                  <td className="p-2 text-zinc-400">{fmtRelative(r.created_at)}</td>
                  <td className="p-2 tabnum text-amber-300">#{String(r.item_number).padStart(3, "0")}</td>
                  <td className="p-2 uppercase text-zinc-400">{r.reason}</td>
                </tr>
              ))}
              {rotations.length === 0 && (
                <tr>
                  <td colSpan={3} className="p-4 text-center text-zinc-500">
                    NO_ROTATIONS · cap not yet reached
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
      </div>
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="border border-[#27272A] bg-[#0E0E0E] p-3">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
        {label}
      </div>
      <div className="text-2xl font-mono mt-1 tabnum">{value}</div>
      <div className="text-[11px] text-zinc-500 font-mono mt-1">{sub}</div>
    </div>
  );
}

